/*
 * This file is part of the Det Norske Teatret application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { type ComponentProps } from 'react';
import Picture from 'components/Picture/Picture';
import Text from '../Text/Text';
import './ImageText.scss';

interface ImageTextProps {
  layout?: 'left' | 'right';
  dropcap?: boolean;
  text?: string;
  image?: (ComponentProps<typeof Picture> & { description?: string }) | null;
}

/**
 * This is the ImageText component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 */
function ImageText({
  layout = 'left',
  text = '',
  image = null,
  dropcap = false,
}: ImageTextProps) {
  function renderImage() {
    if (!image) {
      return <div className="empty" />;
    }

    const { description, ...rest } = image;

    function renderDescription() {
      if (!description) return null;

      return <figcaption dangerouslySetInnerHTML={{ __html: description }} />;
    }

    return (
      <figure className="image" appear="true">
        <Picture {...rest} />
        {renderDescription()}
      </figure>
    );
  }

  if (layout === 'left') {
    return (
      <div className="block image-text layout-left">
        {renderImage()}
        <Text dropcap={dropcap}>{text}</Text>
      </div>
    );
  }

  return (
    <div className="block image-text layout-right">
      <Text dropcap={dropcap}>{text}</Text>
      {renderImage()}
    </div>
  );
}

export default ImageText;
